import type { App } from 'vue'
import PrimeVue from 'primevue/config'
import Aura from '@primeuix/themes/aura'
import { definePreset } from '@primeuix/themes'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'
import 'primeicons/primeicons.css'

/**
 * El preset de PrimeVue sale de los dos colores de marca, para que un botón y
 * el sidebar no sean dos terracotas distintas.
 *
 * Dos rampas, las dos derivadas y con sus contrastes medidos:
 *
 *  - `primary` — la terracota `#c04e37` en el paso 500, aclarada hacia la
 *    crema y oscurecida hacia una sombra que conserva el matiz.
 *  - `surface` — neutros CÁLIDOS desde la crema `#f8f6eb`. Los grises de Aura
 *    son fríos y al lado de la terracota se ven sucios; estos comparten su
 *    temperatura.
 *
 * Ojo con `surface.500` (#8d8b82): 3.15:1 sobre la crema. Vale para un icono o
 * un borde, no para texto — ahí va el 600 (4.70:1).
 */
const VetaliaPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f5ece0',
      100: '#f0ded2',
      200: '#e7c4b5',
      300: '#dca291',
      400: '#ce7864',
      500: '#c04e37',
      600: '#a3432f',
      700: '#8a3928',
      800: '#702f21',
      900: '#57251a',
      950: '#471f16',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f8f6eb',
          100: '#eceadf',
          200: '#dfddd3',
          300: '#cbc9bf',
          400: '#aaa89f',
          500: '#8d8b82',
          600: '#716e66',
          700: '#58554e',
          800: '#44413a',
          900: '#35322c',
          950: '#2b2822',
        },
      },
    },
  },
})

export function installPrimeVue(app: App) {
  app.use(PrimeVue, {
    ripple: true,
    theme: {
      preset: VetaliaPreset,
      options: {
        darkModeSelector: '.dark',
        cssLayer: { name: 'primevue', order: 'theme, base, primevue, utilities' },
      },
    },
    locale: {
      accept: 'Aceptar',
      reject: 'Rechazar',
      cancel: 'Cancelar',
      clear: 'Limpiar',
      apply: 'Aplicar',
      choose: 'Elegir',
      dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
      dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
      dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
      monthNames: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
      ],
      monthNamesShort: [
        'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
        'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
      ],
      today: 'Hoy',
      weekHeader: 'Sm',
      firstDayOfWeek: 1,
      dateFormat: 'dd/mm/yy',
      emptyMessage: 'No hay resultados',
      emptyFilterMessage: 'Sin coincidencias',
    },
  })

  app.use(ToastService)
  app.use(ConfirmationService)
  app.directive('tooltip', Tooltip)
}
